import { API } from 'aws-amplify';

/**
 * Fetch ALL pages of an AppSync list/connection query by following `nextToken`.
 *
 * This centralizes the do/while pagination pattern that is duplicated across the
 * app (e.g. Home.js) so list views stop silently truncating at the first page.
 *
 * Design goals:
 *  - Drop-in: same result shape as a single API.graphql list call (an array of items).
 *  - Fast: large `pageLimit` (fewer round trips), optional progressive rendering via
 *    `onPage`, and a `maxPages` safety cap so a huge table can never hang the UI.
 *  - Safe: auto-detects the connection field, tolerates missing fields, and filters
 *    soft-deleted rows by default (callers usually already do this server-side).
 *
 * @param {string|object} query - GraphQL query (string or gql document) passed to API.graphql.
 * @param {object} [variables={}] - Query variables (e.g. { filter, ...gsiArgs }). `limit`
 *        and `nextToken` are managed internally and will override any provided values.
 * @param {object} [options]
 * @param {number} [options.pageLimit=1000] - Items scanned per page request.
 * @param {number} [options.maxPages=Infinity] - Hard cap on page requests (runaway backstop).
 * @param {boolean} [options.includeDeleted=false] - Keep rows with `_deleted === true`.
 * @param {(pageItems: any[], cumulativeItems: any[]) => void} [options.onPage] - Called after
 *        each page so the UI can render progressively (paint page 1 instantly, stream the rest).
 * @returns {Promise<any[]>} All items across all pages.
 */
export const fetchAllPages = async (query, variables = {}, options = {}) => {
  const {
    pageLimit = 1000,
    maxPages = Infinity,
    includeDeleted = false,
    onPage = null,
  } = options;

  let items = [];
  let nextToken = null;
  let pageCount = 0;

  do {
    const result = await API.graphql({
      query,
      variables: { ...variables, limit: pageLimit, nextToken },
    });

    // Auto-detect the connection field (e.g. "listReports", "actionItemsByReportID").
    const data = result?.data || {};
    const dataKeys = Object.keys(data);
    const resultKey =
      dataKeys.find((k) => data[k] && Array.isArray(data[k].items)) || dataKeys[0];
    const connection = (resultKey && data[resultKey]) || {};

    let pageItems = connection.items || [];
    if (!includeDeleted) {
      pageItems = pageItems.filter((item) => !item?._deleted);
    }

    items = items.concat(pageItems);
    pageCount += 1;

    if (typeof onPage === 'function') {
      try {
        onPage(pageItems, items);
      } catch (e) {
        // A render-callback error must never break data fetching.
        // eslint-disable-next-line no-console
        console.error('fetchAllPages onPage callback error:', e);
      }
    }

    nextToken = connection.nextToken;

    if (pageCount >= maxPages) {
      if (nextToken) {
        // eslint-disable-next-line no-console
        console.warn(
          `fetchAllPages: reached maxPages (${maxPages}); returning partial result of ${items.length} items.`
        );
      }
      break;
    }
  } while (nextToken);

  return items;
};

export default fetchAllPages;
